import { Component, Input, OnInit, OnDestroy, Inject } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';
import { Subscription, Observable, of, skip, filter } from 'rxjs';
import * as am4core from '@amcharts/amcharts4/core';
import { LegendDataItem } from '@amcharts/amcharts4/maps';
import moment from 'moment-timezone';
import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { PreferencesModel } from '../../../../Core/Users/models/preferences.model';
import { GaugeChartModel } from '../../../../Common/Charts/models/gaugeChart/gauge-chart.model';
import { GaugeChartOptionsModel } from '../../../../Common/Charts/models/gaugeChart/gauge-chart-options.model';
import { LineChartModel } from '../../../../Common/Charts/models/lineChart/line-chart.model';
import { LineChartOptionsModel } from '../../../../Common/Charts/models/lineChart/line-chart-options.model';
import { SeriesModel } from '../../../../Common/Charts/models/series.model';
import { LineSeriesModel } from '../../../../Common/Charts/models/lineChart/line-series.model';
import { abbreviateNumber, numberWithSeparators } from '../../../../Common/Misc/helpers/formatting/number.helper';
import { chartColors } from '../../../../Common/Charts/enums/chart-colors.enum';
import {
    MentionSavingUsageDashboardService,
    QuotaUsageOverTimeSeries,
    MentionSavingSeriesType
} from '../../services/mention-saving-usage-dashboard.service';
import { userAppPreferencesDefault } from '../../../../Core/Users/data/user-app-preferences-default';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { MentionSavingQuota } from '../../../../api/monitoring/model/monitoring-account-configuration-api.model';
import { MonitorConfigService } from '../../../../Common/MonitorShared/services/monitor-config.service';
@Component({
    selector: 'agpr-mention-saving-usage-dashboard',
    templateUrl: './mention-saving-usage-dashboard.component.html',
    styleUrls: ['./mention-saving-usage-dashboard.component.scss'],
    standalone: false
})
export class MentionSavingUsageDashboardComponent extends AbstractDialogComponent implements OnInit, OnDestroy {

    id: string = 'quotaUsageDashboard';
    readonly buttonTypes = ButtonTypesEnum;
    readonly defaultChartColors = userAppPreferencesDefault.reportsPrefs.defaultChartColors;

    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;
    @Input() coverNav: boolean = false;

    subscriptions: Subscription[] = [];
    hideSlideOverlay = false;
    preferences: PreferencesModel;

    quotaInfo: MentionSavingQuota;
    hasQuota: boolean = true;
    totalQuota: string;
    totalQuotaUsed: string;
    percentOfQuotaUsed: string;
    daysRemaining: number;
    quotaResetDate: string;

    dailyAverageUsageLoading: boolean = true;
    dailyAverageUsageChart: GaugeChartModel;
    dailyAverageUsageChartOptions: GaugeChartOptionsModel;

    quotaUsageOverTimeLoading: boolean = true;
    quotaUsageOverTimeData: QuotaUsageOverTimeSeries[];
    quotaUsageOverTimeChart: LineChartModel;
    quotaUsageOverTimeChartOptions: LineChartOptionsModel;

    constructor(
        @Inject(APP_BASE_HREF) public appBaseHref: string,
        public coreDialogService: CoreDialogService,
        public localizationService: LocalizationService,
        public currentUserStateService: CurrentUserStateService,
        public monitorConfigService: MonitorConfigService,
        private mentionSavingUsageDashboardService: MentionSavingUsageDashboardService,
    ) {
        super(coreDialogService, 'QuotaUsageDashboard');
        this.monitorConfigService.refreshMonitoringConfigModel();
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.currentUserStateService.getPreferences().pipe(
                filter(x => !!x),
            ).subscribe(prefs => {
                this.preferences = prefs;
            }),
            this.monitorConfigService.getMentionSavingQuota().subscribe(quotaInfo => {
                if(quotaInfo) {
                    this.quotaInfo = quotaInfo;
                    if (quotaInfo.savingQuota === -1) {
                        this.hasQuota = false;
                    }
                    this.generateDailyAverageUsageChart();
                    this.calculateQuotaStats();
                }
                this.dailyAverageUsageLoading = false;
            }),
            this.mentionSavingUsageDashboardService.getQuotaUsageOverTime().subscribe(quotaUsageOverTimeData => {
                if(quotaUsageOverTimeData) {
                    this.quotaUsageOverTimeData = quotaUsageOverTimeData;
                    this.generateQuotaUsageOverTimeChart();
                }
                this.quotaUsageOverTimeLoading = false;
            }),

        );
    }

    generateDailyAverageUsageChart() {
        this.dailyAverageUsageChart = null;

        const quotaInfo = this.quotaInfo;

        let dailyAverageUsage: number = 0;
        if (this.hasQuota) {
            const daysInMonth = moment.utc().daysInMonth();
            const daysElapsedThisMonth = moment.utc().date();
            const availableDailyQuota = (quotaInfo.savingQuota + quotaInfo.additionalMentionQuota) / daysInMonth;
            const usedDailyQuota = quotaInfo.savingQuotaUsed / daysElapsedThisMonth;
            dailyAverageUsage = availableDailyQuota === 0 ? 0 : (usedDailyQuota / availableDailyQuota) * 100;
        }

        const chartSeries: SeriesModel[] = [
            {
                label: '',
                dataProvider: of(
                    [
                        {
                            label: this.localizationService.transform('Monitoring.mentionSavingUsageDashboard.dailyAverageUsage'),
                            xValue: dailyAverageUsage
                        }
                    ]
                )
            }
        ];

        this.dailyAverageUsageChartOptions = {
            minXDegrees: -210,
            maxXDegrees: 30,
            percentLabelOptions: {
                fontSize: 24,
                dy: -20
            },
            seriesLabelOptions: {
                fontSize: 16,
                dy: 30,
                textAlign: 'middle'
            }
        };

        this.dailyAverageUsageChart = new GaugeChartModel(chartSeries, this.dailyAverageUsageChartOptions);
    }

    calculateQuotaStats(): void {
        const dateFormat = this.preferences?.getDateFormatString(true);
        if (!dateFormat) {
            return;
        }

        const totalQuota = this.hasQuota ? this.quotaInfo.savingQuota + this.quotaInfo.additionalMentionQuota : 0;
        this.totalQuota = this.hasQuota ? abbreviateNumber(totalQuota) : '--';
        const totalQuotaUsed = this.quotaInfo.savingQuotaUsed;
        this.totalQuotaUsed = abbreviateNumber(totalQuotaUsed);
        this.percentOfQuotaUsed = !this.hasQuota
            ? '--'
            : totalQuota === 0
                ? '0'
                : Math.round(totalQuotaUsed / totalQuota * 100) + '%';
        const quotaResetDate = moment.utc(this.quotaInfo.savingQuotaEnd).add(1, 'day');
        this.quotaResetDate = quotaResetDate.format(dateFormat);
        this.daysRemaining = quotaResetDate.diff(moment().utc().startOf('day'), 'days');
    }

    generateQuotaUsageOverTimeChart() {
        this.quotaUsageOverTimeChart = null;
        const {savingQuota, additionalMentionQuota, savingQuotaEnd} = this.quotaInfo;

        this.quotaUsageOverTimeChartOptions = {
            precision: 0,
            numberFormat: '#a',
            xValueLabel: this.localizationService.transform('Monitoring.mentionSavingUsageDashboard.dayOfTheMonth'),
            yValueLabel: this.localizationService.transform('Monitoring.mentionSavingUsageDashboard.mentionCount'),
            minYValue: 0,
            minXValue: 0,
            maxXValue: moment.utc(savingQuotaEnd).date(),
            strictMinMax: true
        };

        const allUsageLineData = this.quotaUsageOverTimeData.find(line => line?.seriesType === MentionSavingSeriesType.ALL)?.data;
        if (allUsageLineData) {
            const maxYValue = Math.max(savingQuota + additionalMentionQuota, allUsageLineData[allUsageLineData.length - 1].yValue);
            if (maxYValue > 0) {
                this.quotaUsageOverTimeChartOptions.maxYValue = maxYValue * 1.1;
            }
        }
        if (this.hasQuota) {
            this.quotaUsageOverTimeChartOptions.horizontalLines = [
                {
                    position: savingQuota + additionalMentionQuota,
                    color: '#f26c79',
                    strokeWidth: 2,
                    tooltip: this.localizationService.transform('Monitoring.mentionSavingUsageDashboard.quota')
                }
            ];
        }
        const today = moment.utc().startOf('day').date();
        this.quotaUsageOverTimeChartOptions.verticalLines = [
            {
                position: today,
                color: '#66b68f',
                strokeWidth: 2,
                tooltip: this.localizationService.transform('Monitoring.mentionSavingUsageDashboard.today')
            }
        ];

        const chartSeries: LineSeriesModel[] = this.quotaUsageOverTimeData.map(line => {
            return {
                label: line.label,
                dataProvider: of(line.data),
                useProjection: true,
                projectionStrokeSize: 1,
                projectionStrokeDashArray: '8,3',
                color: line?.seriesType === MentionSavingSeriesType.ALL
                    ? '#1f3b9e'
                    : line?.seriesType === MentionSavingSeriesType.OTHER
                        ? '#7a7a7a'
                        : undefined,
            };
        });

        const chart = new LineChartModel(chartSeries, this.quotaUsageOverTimeChartOptions);
        chart.ready.subscribe(isReady => {
            if(isReady) {
                const template = chart.amchart.legend.itemContainers.template;

                if(!template.tooltip) {
                    template.tooltip = new am4core.Tooltip();
                }

                template.tooltip.getFillFromObject = false;
                template.tooltip.cursorOverStyle = am4core.MouseCursorStyle.pointer;
                template.tooltip.label.interactionsEnabled = true;
                template.tooltip.label.fill = am4core.color(chartColors.agprText);
                template.tooltip.background.fill = am4core.color(chartColors.agprWhite);
                template.tooltip.background.fillOpacity = 1;
                template.tooltip.background.stroke = am4core.color('#ced4da');
                template.tooltip.background.cornerRadius = 8;
                template.tooltip.background.filters.clear();
                template.tooltip.keepTargetHover = true;

                template.tooltipHTML = ''; // init tooltipHTML
                template.adapter.add('tooltipHTML', (text, target) => {
                    if(text) {
                        return text;
                    } else {
                        const dataItem = target.tooltipDataItem as LegendDataItem;
                        const line = this.quotaUsageOverTimeData.find(itm => itm.label === dataItem.label.currentText);
                        const series = chart.amchart.series.values.find(itm => itm.name === dataItem.label.currentText);
                        const url = line?.id ? this.appBaseHref + 'Monitoring/topics/topic/' + line.id + '/update' : null ;
                        return url
                            ? `<div style="margin: 10px; font-size: 0.875rem;">
                                    <div style="border-bottom: 1px solid #ced4da; padding-bottom: 10px">
                                        <i
                                            class="fa-solid fa-code-commit"
                                            style="color: ${(series.stroke as am4core.Color).hex}; margin-right: 10px;"
                                        ></i>
                                        <span style="font-weight: 600">
                                            ${line.label}
                                        </span>
                                    </div>
                                    <div style="display: flex; justify-content:flex-end; margin-top: 10px">
                                        <a
                                            class="mentionSavingUsageDashboard-editTopicLink"
                                            style="color: inherit"
                                            href="${url}"
                                            target="_blank"
                                        >
                                            Edit Topic
                                        </a>
                                    </div>
                                </div>`
                            : '';
                    }
                });
            }
        });
        this.quotaUsageOverTimeChart = chart;
    }

    close() {
        this.hideSlideOverlay = true;
    }

    handleVisibleChange(isVisible) {
        if(!isVisible) {
            this.closeDialog();
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
